import {makeObservable, observable, action, reaction, IReactionDisposer} from 'mobx';

import {getCurrentUserIdToken} from "../../utils/user";
import {
    httpLikeRequestTroughWebsocket
} from "./makeApiServerRequest";


import {userDeviceId} from "../../sync/UserDeviceId";
import {PM_ACTIONS, ValueOf} from "./types";
import {handleUnauthorizedSession} from "../../actions/app/handleUnauthorizedSession";

import {EventEmitter, GeneralEventData} from "../../volatile/EventEmitter";
import {IAuthService} from "../../platform/services/auth/AuthService";
import {
    reportServerConnectionFailure,
    reportServerConnectionRestored,
} from "./serverAvailability";


export const ConnectionState = {
    READY: 10,
    WAITING_AUTH: 11,
    WAITING_SERVER: 12,
    WAITING_NETWORK: 13,
}

export type ConnectionStateType = ValueOf<typeof ConnectionState>;

const DEFAULT = {
    pingInterval: 59000,
    maxReconnectionDelay: 30000,
    minReconnectionDelay: 1000 + Math.random() * 4000,
    minUptime: 5000,
    reconnectionDelayGrowFactor: 1.3,
    connectionTimeout: 4000,
    maxRetries: Infinity,
    maxEnqueuedMessages: Infinity,
    startClosed: false,
    debug: false,
    tabResumeReconnectThreshold: 5 * 60 * 1000,
};

export class NetworkError extends Error {
    name = 'NetworkError';
}

export class ErrorEvent extends Event {
    public message: string;
    public error: Error;

    constructor(error: Error, target: any) {
        super('error', target);
        this.message = error.message;
        this.error = error;
    }
}


export type WS_EventMap = {
    'close': GeneralEventData<'close'>;
    'open': GeneralEventData<'open'>;
};

export type WS_EVENT = 'open' | 'close';

export class WsServerConnection  extends EventEmitter<WS_EVENT, WS_EventMap> {
    private _ws?: WebSocket;
    private _authExp?: number;
    private _processingAuth: boolean = false;
    private _shouldReconnect: boolean = true;
    private _stateQueue: ConnectionStateType[] = [ConnectionState.WAITING_SERVER];
    private messageQueue: any[] = [];
    private messageHandlers: ((msg: any) => void)[] = [];
    private authService: IAuthService;

    hasUnSyncedChanges: boolean = false;

    private _retryCount = -1;
    private _uptimeTimeout: any;
    private _connectTimeout: any;
    private _pingInterval: any;
    private _connectLock = false;
    private _closeCalled = false;
    private _hasStarted = false;
    private _hiddenAt?: number;
    private networkDisposer?: IReactionDisposer;

    constructor(authService: IAuthService) {
        super();
        this.authService = authService;
        makeObservable(this, {
            hasUnSyncedChanges: observable,
            setHasUnSyncedChanges: action,
        });
        this._addPageLifecycleListeners();
    }

    doesWaitsFor = (state: ConnectionStateType) => {
        return this._stateQueue.includes(state);
    }

    waitFor = (state: ConnectionStateType) => {
        if (!this._stateQueue.includes(state)) {
            this._stateQueue.push(state);
        }
    }

    clearWaitFor = (state: ConnectionStateType) => {
        if (this._stateQueue.includes(state)) {
            this._stateQueue = this._stateQueue.filter(_state => _state !== state);
        }
        if (this._stateQueue.length === 0
            || (this._stateQueue.length === 1 && this.doesWaitsFor(ConnectionState.WAITING_AUTH))
        ) {
            if (!this.hasValidAuth) {
                this.waitFor(ConnectionState.WAITING_AUTH);
                this._auth().then(() => this.flushQueue());
            } else {
                this.flushQueue();
            }
        }
    }

    setHasUnSyncedChanges = (hasUnSyncedChanges: boolean) => {
        this.hasUnSyncedChanges = hasUnSyncedChanges;
    }

    watchNetworkState(getIsOnline: () => boolean) {
        this.networkDisposer?.();
        this.networkDisposer = reaction(
            () => getIsOnline(),
            (isOnline) => {
                if (isOnline) {
                    if (this.doesWaitsFor(ConnectionState.WAITING_NETWORK)) {
                        this.clearWaitFor(ConnectionState.WAITING_NETWORK);
                    }
                } else {
                    this.waitFor(ConnectionState.WAITING_NETWORK);
                }
            },
            {fireImmediately: true}
        );
    }

    public close(code = 1000, reason?: string) {
        this.networkDisposer?.();
        this.networkDisposer = undefined;
        this._removePageLifecycleListeners();
        this._closeCalled = true;
        this._shouldReconnect = false;
        this._hasStarted = false;
        this._clearTimeouts();
        if (!this._ws) {
            this._debug('close enqueued: no ws instance');
            return;
        }
        if (this._ws.readyState === WebSocket.CLOSED) {
            this._debug('close: already closed');
            return;
        }
        this._ws.close(code, reason);
    }

    get hasValidAuth() {
        // @ts-ignore
        return this._authExp && this._authExp > Math.floor(Date.now()/1000);
    }

    sendMsg = (msg: any) => {
        this._debug('sendMsg', msg);
        if (this.doesWaitsFor(ConnectionState.WAITING_NETWORK)) {
            throw new NetworkError('network is not available');
        }
        if (this._ws?.readyState === WebSocket.OPEN
            && !this.doesWaitsFor(ConnectionState.WAITING_SERVER)
        ) {
            if (!this.hasValidAuth && msg.a !== PM_ACTIONS.AUTH) {
                this._debug('sendMsg - has no valid auth');
                this.waitFor(ConnectionState.WAITING_AUTH);
                this._auth().then(() => this.sendMsg(msg));
            } else {
                this._debug('sentMsg');
                this._ws!.send(JSON.stringify(msg));
            }

            return true;
        } else {
            this._debug('queue sentMsg', this._stateQueue);
            this.queueMsg(msg);
        }
        return false;
    }

    queueMsg(msg: any) {
        this.messageQueue.push(msg);
        this.setHasUnSyncedChanges(true);
    }

    flushQueue = () => {
        while (this.messageQueue.length > 0) {
            const msg = this.messageQueue.shift();
            const successfullySent = this.sendMsg(msg);
            if (!successfullySent) {
                break;
            }
        }

        this.setHasUnSyncedChanges(false);
    }

    onMsg = (cb: (msg: any) => void) => {
        if (!this.messageHandlers.includes(cb)) {
            this.messageHandlers.push(cb);
        }
    }

    offMsg = (cb: (msg: any) => void) => {
        const index = this.messageHandlers.indexOf(cb);
        if (index > -1) {
            this.messageHandlers.splice(index, 1);
        }
    }

    private _debug(...args: any[]) {
        if ((window as any).DEBUG === 3) {
            // not using spread because compiled version uses Symbols
            // tslint:disable-next-line
            console.log.apply(console, ['pm ws connection>', ...args]);
        }
    }

    private _acceptOpen() {
        this._debug('accept open');
        this._retryCount = 0;
    }

    private _addPageLifecycleListeners() {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        if (document.visibilityState === 'hidden') {
            this._hiddenAt = Date.now();
        }

        document.addEventListener('visibilitychange', this._handleVisibilityChange);
        window.addEventListener('online', this._handleOnline);
        window.addEventListener('offline', this._handleOffline);
    }

    private _removePageLifecycleListeners() {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        document.removeEventListener('visibilitychange', this._handleVisibilityChange);
        window.removeEventListener('online', this._handleOnline);
        window.removeEventListener('offline', this._handleOffline);
    }

    private _handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            this._hiddenAt = Date.now();
            return;
        }

        const hiddenAt = this._hiddenAt;
        this._hiddenAt = undefined;

        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            this.waitFor(ConnectionState.WAITING_NETWORK);
            return;
        }

        if (hiddenAt && Date.now() - hiddenAt >= DEFAULT.tabResumeReconnectThreshold) {
            this.reconnect('tab resumed after inactivity');
            return;
        }

        this._checkConnectionLiveness();
    }

    private _handleOnline = () => {
        if (this.doesWaitsFor(ConnectionState.WAITING_NETWORK)) {
            this.clearWaitFor(ConnectionState.WAITING_NETWORK);
        }
        this._checkConnectionLiveness();
    }

    private _handleOffline = () => {
        this.waitFor(ConnectionState.WAITING_NETWORK);
    }

    public reconnect(reason = 'manual reconnect') {
        if (!this._hasStarted || !this._shouldReconnect || this._closeCalled) {
            return;
        }

        this._debug('reconnect', reason);
        this._authExp = undefined;
        this.waitFor(ConnectionState.WAITING_SERVER);

        if (!this._ws || this._ws.readyState === WebSocket.CLOSED) {
            this.connect();
            return;
        }

        this._disconnect();
    }

    private _checkConnectionLiveness() {
        if (!this._shouldReconnect || this._closeCalled || this.doesWaitsFor(ConnectionState.WAITING_NETWORK)) {
            return;
        }

        if (!this._ws || this._ws.readyState === WebSocket.CLOSED || this._ws.readyState === WebSocket.CLOSING) {
            this.reconnect('socket is not open on page lifecycle event');
        }
    }

    private _increaseRetries = () => {
        if (this._retryCount >= DEFAULT.maxRetries) {
            this._debug('max retries reached', this._retryCount, '>=', DEFAULT.maxRetries);
            return false;
        }
        this._retryCount++;
        return true;
    }

    connect() {
        this._hasStarted = true;
        if (this._connectLock || !this._shouldReconnect) {
            return;
        }
        this._connectLock = true;

        if (this._retryCount >= DEFAULT.maxRetries) {
            this._debug('max retries reached', this._retryCount, '>=', DEFAULT.maxRetries);
            return;
        }
        this._retryCount++;
        this._debug('connect schedule', this._retryCount);
        this._removeListeners();
        this._authExp = undefined;

        this._wait()
            .then(() => {
                // close could be called before creating the ws
                if (this._closeCalled) {
                    return;
                }
                this._debug('connect');
                this._ws = new WebSocket(import.meta.env.VITE_API_BASE_URL);

                this._connectLock = false;
                this._addListeners();

                this._connectTimeout = setTimeout(() => this._handleTimeout(), DEFAULT.connectionTimeout);
            });

    }

    private _handleTimeout() {
        this._debug('timeout event');
        this._handleError(new ErrorEvent(new Error('TIMEOUT'), this));
    }

    _addListeners = () => {
        if (!this._ws) {
            return;
        }
        this._debug('addListeners');
        this._ws.addEventListener('open', this._handleOpen);
        this._ws.addEventListener('message', this._handleMessage);
        this._ws.addEventListener('error', this._handleError);
        this._ws.addEventListener('close', this._handleClose);
    }

    _auth = async () => {
        if (this._processingAuth) {
            this._retryCount++;
            return this._wait();
        }
        try {
            this._processingAuth = true;
            const actualToken = await getCurrentUserIdToken(this.authService);

            const response = await httpLikeRequestTroughWebsocket(
                this,
                'auth',
                'auth',
                {
                    a: 'auth',
                    t: actualToken,
                    ud: userDeviceId,
                },
                (msg) => msg.a,
            );

            this._authExp = response.exp;
            this._processingAuth = false;
            this.clearWaitFor(ConnectionState.WAITING_AUTH);
        } catch(error: unknown) {
            this._processingAuth = false;
            if (error instanceof NetworkError) {
                this.waitFor(ConnectionState.WAITING_NETWORK);
            } else {
                throw error;
            }
        }
    }

    private _wait(): Promise<void> {
        return new Promise(resolve => {
            setTimeout(resolve, this._getNextDelay());
        });
    }

    _handleOpen = () => {
        this._debug('open event');

        clearTimeout(this._connectTimeout);
        this._uptimeTimeout = setTimeout(() => this._acceptOpen(), DEFAULT.minUptime);
        this.clearWaitFor(ConnectionState.WAITING_SERVER);
        reportServerConnectionRestored();

        this._pingInterval = setInterval(() => {
            if (this._ws?.readyState === WebSocket.OPEN) {
                this._ws.send(JSON.stringify({ p: 'ka' }));
            }
        }, DEFAULT.pingInterval);
        this.dispatch({type: 'open'});
    }

    _handleClose = () => {
        this.waitFor(ConnectionState.WAITING_SERVER);
        if (this._shouldReconnect && !this._closeCalled) {
            reportServerConnectionFailure(new Error('WebSocket connection closed'));
        }
        if (this._shouldReconnect) {
            this.connect();
        }

        clearInterval(this._pingInterval);
        this.dispatch({type: 'close'});
    }

    _handleMessage = (event: MessageEvent) => {
        const msg = JSON.parse(event.data);
        this._debug('_handleMessage', msg);
        if (msg.s && msg.s === 401) {
            this._authExp = undefined;
            this._auth().catch((error) => {
                void handleUnauthorizedSession(error);
            });
        }

        this.messageHandlers.forEach(handler => handler(msg));
    }

    _handleError = (event: Event) => {
        console.error(event);
        this._debug('error event', event);
        reportServerConnectionFailure(event instanceof ErrorEvent ? event.error : new Error('WebSocket connection error'));
        this._disconnect();

        this._debug('exec error listeners');

        this.connect();
    }

    _disconnect() {
        this._clearTimeouts();
        if (!this._ws) {
            return;
        }

        this._removeListeners();
        try {
            this._ws.close();
            this._handleClose();
        } catch (error) {
            console.error('error', error);
            // ignore
        }
    }

    private _removeListeners() {
        if (!this._ws) {
            return;
        }
        this._debug('removeListeners');
        this._ws.removeEventListener('open', this._handleOpen);
        this._ws.removeEventListener('close', this._handleClose);
        this._ws.removeEventListener('message', this._handleMessage);
        // @ts-ignore
        this._ws.removeEventListener('error', this._handleError);
    }

    private _clearTimeouts() {
        clearTimeout(this._connectTimeout);
        clearTimeout(this._uptimeTimeout);
    }

    private _getNextDelay() {
        let delay = 0;
        if (this._retryCount > 0) {
            delay =
                DEFAULT.minReconnectionDelay * Math.pow(DEFAULT.reconnectionDelayGrowFactor, this._retryCount - 1);
            if (delay > DEFAULT.maxReconnectionDelay) {
                delay = DEFAULT.maxReconnectionDelay;
            }
        }
        this._debug('next delay', delay);
        return delay;
    }
}
